import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { logger } from '../lib/logger';
import { API_BASE_URL } from '../config/api';

class ApiService {
  private client: AxiosInstance;
  private static instance: ApiService;
  // Single-flight access-token renewal. Every concurrent 401 shares the
  // same refresh operation so a token expiry cannot create a refresh storm.
  private refreshPromise: Promise<boolean> | null = null;

  private constructor() {
    // Canonical API root. API_BASE_URL normalizes both an origin-only value
    // and an explicit /api value to the Express router root.
    this.client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      timeout: 120000,
    });

    this.setupInterceptors();
  }

  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }

    return ApiService.instance;
  }

  private setupInterceptors(): void {
    this.client.interceptors.request.use(
      this.handleRequest.bind(this),
      this.handleRequestError.bind(this)
    );

    this.client.interceptors.response.use(
      this.handleResponse.bind(this),
      this.handleResponseError.bind(this)
    );
  }

  private handleRequest(
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig {
    logger.debug(
      `API Request: ${config.method?.toUpperCase()} ${config.url}`
    );

    return config;
  }

  private handleRequestError(error: any): Promise<any> {
    logger.error('API Request Error:', error);
    return Promise.reject(error);
  }

  private handleResponse(response: any): any {
    logger.debug(
      `API Response: ${response.status} ${response.config.url}`
    );

    return response;
  }

  /**
   * Renew the short-lived access token using the HttpOnly refresh cookie.
   *
   * This deliberately uses plain axios rather than this.client so /auth/refresh
   * cannot recursively enter the response interceptor.
   *
   * Concurrent callers share one promise: a single expired access token must
   * produce exactly one refresh request.
   */
  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshPromise) {
      this.refreshPromise = axios
        .post('/auth/refresh', {}, {
          baseURL: API_BASE_URL,
          withCredentials: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          timeout: 15000,
        })
        .then(() => true)
        .catch(() => false)
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  private async handleResponseError(error: AxiosError): Promise<any> {
    const status = error.response?.status;
    const data = error.response?.data as any;

    if (status === 401) {
      const originalConfig = error.config as
        | (InternalAxiosRequestConfig & { _authRetry?: boolean })
        | undefined;

      const requestUrl = String(originalConfig?.url || '');

      const isLoginRequest =
        requestUrl.includes('/auth/login');

      const isRefreshRequest =
        requestUrl.includes('/auth/refresh');

      // Invalid credentials belong to the login form. A failed refresh is the
      // authoritative signal that the longer-lived authenticated session has
      // actually ended. Neither route is eligible for recursive renewal.
      if (!isLoginRequest && !isRefreshRequest && originalConfig) {
        if (!originalConfig._authRetry) {
          originalConfig._authRetry = true;

          const refreshed = await this.refreshAccessToken();

          if (refreshed) {
            logger.debug(
              `Access token renewed; retrying request once: ${requestUrl}`
            );

            return this.client.request(originalConfig);
          }
        }

        logger.warn(
          'Authenticated session could not be renewed'
        );

        window.dispatchEvent(
          new CustomEvent('coconnect:auth-expired')
        );
      }
    }

    if (status === 403) {
      logger.error(
        'Access forbidden:',
        data?.error || 'Insufficient permissions'
      );
    }

    if (status && status >= 500) {
      logger.error(
        'Server error:',
        data?.error || 'Internal server error'
      );
    }

    return Promise.reject(error);
  }

  get<T = any>(url: string, config?: any): Promise<T> {
    return this.client
      .get<T>(url, config)
      .then(res => res.data);
  }

  post<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<T> {
    return this.client
      .post<T>(url, data, config)
      .then(res => res.data);
  }

  put<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<T> {
    return this.client
      .put<T>(url, data, config)
      .then(res => res.data);
  }

  delete<T = any>(
    url: string,
    config?: any
  ): Promise<T> {
    return this.client
      .delete<T>(url, config)
      .then(res => res.data);
  }

  patch<T = any>(
    url: string,
    data?: any,
    config?: any
  ): Promise<T> {
    return this.client
      .patch<T>(url, data, config)
      .then(res => res.data);
  }

  getClient(): AxiosInstance {
    return this.client;
  }
}

export const apiClient =
  ApiService.getInstance().getClient();

export const api =
  ApiService.getInstance();

export default api;
